import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Inbox, ChefHat, Flame, ArrowRight, Sparkles, Star, Beef, Leaf, Fish } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FILTERS = [
  { key: 'all', label: 'Toutes' },
  { key: 'featured', label: 'Coup de cœur du coach' },
  { key: 'high_protein', label: 'Riche en protéines' },
  { key: 'light', label: 'Léger (<400 kcal)' },
  { key: 'viande', label: 'Viande' },
  { key: 'poisson', label: 'Poisson' },
  { key: 'vegetarien', label: 'Végétarien' },
];

const FILTER_ICONS = { featured: Star, high_protein: Beef, light: Leaf, viande: Beef, poisson: Fish, vegetarien: Leaf };

const RecipeGalleryPage = () => {
  const [recipes, setRecipes] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/personal-recipes/gallery`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setRecipes(d.recipes || []))
      .catch(() => setRecipes([]));
  }, []);

  const filtered = useMemo(() => {
    if (!recipes) return [];
    if (activeFilter === 'featured') return recipes.filter((r) => r.featured);
    if (activeFilter === 'high_protein') return recipes.filter((r) => r.high_protein);
    if (activeFilter === 'light') return recipes.filter((r) => (r.macros?.calories || 0) < 400);
    if (['viande', 'poisson', 'vegetarien'].includes(activeFilter)) return recipes.filter((r) => (r.diet || []).includes(activeFilter));
    return recipes;
  }, [recipes, activeFilter]);

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="recipe-gallery-page">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-primary/20 border border-primary/40 text-primary mb-4"><Sparkles className="w-3.5 h-3.5" /> Communauté Body-Up</span>
        <h1 className="text-3xl sm:text-5xl font-black text-white mb-3">Galerie de recettes</h1>
        <p className="text-zinc-400 mb-8 max-w-xl">Les meilleures recettes générées et partagées par les membres Body-Up, avec leurs macros calibrées.</p>

        {recipes && recipes.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-filter-chips">
            {FILTERS.map((f) => {
              const Icon = FILTER_ICONS[f.key];
              return (
                <button
                  key={f.key} onClick={() => setActiveFilter(f.key)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${activeFilter === f.key ? 'bg-primary border-primary text-white' : 'border-white/10 text-zinc-400 hover:border-primary/40 hover:text-white'}`}
                  data-testid={`gallery-filter-${f.key}`}
                >
                  {Icon && <Icon className="w-3 h-3 inline mr-1 -mt-0.5" />}
                  {f.label}
                </button>
              );
            })}
          </div>
        )}

        {recipes === null && <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}

        {recipes && recipes.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-14 text-center" data-testid="recipe-gallery-empty">
            <Inbox className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">Aucune recette partagée pour l'instant. Soyez le premier !</p>
          </div>
        )}

        {recipes && recipes.length > 0 && filtered.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-14 text-center" data-testid="recipe-gallery-filter-empty">
            <Inbox className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
            <p className="text-zinc-400">Aucune recette ne correspond à ce filtre.</p>
          </div>
        )}

        {filtered.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {filtered.map((r) => (
              <Link key={r.share_token} to={`/recette-partagee/${r.share_token}`}
                className="rounded-2xl overflow-hidden border border-white/10 bg-zinc-900 aspect-[4/5] hover:border-primary/50 transition-colors relative block"
                data-testid={`gallery-recipe-card-${r.share_token}`}>
                {r.image_url ? <img src={r.image_url} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 flex items-center justify-center bg-zinc-800"><ChefHat className="w-6 h-6 text-zinc-600" /></div>}
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent" />
                {r.featured && (
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold bg-yellow-500/90 text-black" data-testid={`gallery-featured-badge-${r.share_token}`}>
                    <Star className="w-2.5 h-2.5 fill-black" /> Coup de cœur
                  </span>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="text-white text-sm font-bold line-clamp-2 leading-snug">{r.name}</p>
                  <p className="text-xs text-zinc-400 mt-1 inline-flex items-center gap-1"><Flame className="w-3 h-3 text-primary" /> {Math.round(r.macros?.calories || 0)} kcal</p>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-14 rounded-2xl bg-gradient-to-br from-primary/20 to-pink-500/10 border border-primary/30 p-6 text-center">
          <p className="text-white font-bold mb-1">Envie de générer votre propre plan alimentaire sur-mesure ?</p>
          <Link to="/calculateur-calories" className="inline-flex items-center gap-2 mt-3 px-6 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-pink-500 hover:scale-105 transition-transform" data-testid="recipe-gallery-cta">
            Découvrir Body-Up <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RecipeGalleryPage;
